import { createAsyncThunk } from '@reduxjs/toolkit';
import { tr } from '../../i18n/i18n';
import {
  IExamReportCardResponse,
  IParentExamListResponse,
} from '../../interface/reportCard.interface';
import {
  IMyStudentIdentityResponse,
  IStudentExamDetailResponse,
} from '../../interface/studentPortalExam.interface';
import { IErrorResponse } from '../../interface/main.interface';
import { studentPortalService } from '../../services/studentPortal';
import { normalizeAxiosError } from '../../utility/axiosErrorHandler';

/**
 * The logged-in student's own identity. Every exam call needs the studentId, and
 * it is not on the session payload — the page resolves it once on mount.
 */
export const fetchMyStudentIdentity = createAsyncThunk<
  IMyStudentIdentityResponse,
  void,
  { rejectValue: IErrorResponse }
>('studentPortalExam/fetch-identity', async (_arg, { rejectWithValue }) => {
  try {
    const response = await studentPortalService.fetchMyStudentIdentity();
    return response.data;
  } catch (err: unknown) {
    return rejectWithValue(
      normalizeAxiosError(
        err,
        tr('student_portal_exam.error_identity', 'Failed to load your student profile.'),
      ),
    );
  }
});

/** The student's exams. Identical payload for a guardian of the same student. */
export const fetchStudentExamList = createAsyncThunk<
  IParentExamListResponse,
  string,
  { rejectValue: IErrorResponse }
>('studentPortalExam/fetch-list', async (studentId, { rejectWithValue }) => {
  try {
    const response = await studentPortalService.fetchStudentExams(studentId);
    return response.data;
  } catch (err: unknown) {
    return rejectWithValue(
      normalizeAxiosError(err, tr('student_portal_exam.error_list', 'Failed to fetch exams.')),
    );
  }
});

/** One exam's subjects. Scores are null until the exam is released. */
export const fetchStudentExamDetail = createAsyncThunk<
  IStudentExamDetailResponse,
  { studentId: string; examId: string },
  { rejectValue: IErrorResponse }
>('studentPortalExam/fetch-detail', async ({ studentId, examId }, { rejectWithValue }) => {
  try {
    const response = await studentPortalService.fetchStudentExamDetail(studentId, examId);
    return response.data;
  } catch (err: unknown) {
    return rejectWithValue(
      normalizeAxiosError(err, tr('student_portal_exam.error_detail', 'Failed to fetch the exam.')),
    );
  }
});

/**
 * The student's report card for one exam.
 * The axios interceptor rejects with the backend body, so the machine-readable
 * `errorCode` is read straight off the error and preserved for the page (a
 * still-being-marked exam answers 400 + REPORT_CARD_NOT_RELEASED).
 */
export const fetchStudentReportCard = createAsyncThunk<
  IExamReportCardResponse,
  { studentId: string; examId: string },
  { rejectValue: IErrorResponse & { errorCode?: string } }
>('studentPortalExam/fetch-report-card', async ({ studentId, examId }, { rejectWithValue }) => {
  try {
    const response = await studentPortalService.fetchStudentReportCard(studentId, examId);
    return response.data;
  } catch (err: unknown) {
    return rejectWithValue({
      ...normalizeAxiosError(
        err,
        tr('student_portal_exam.error_report_card', 'Failed to fetch the report card.'),
      ),
      errorCode: (err as { errorCode?: string })?.errorCode,
    });
  }
});

export const studentPortalExamActions = {
  fetchMyStudentIdentity,
  fetchStudentExamList,
  fetchStudentExamDetail,
  fetchStudentReportCard,
};
